import * as THREE from 'three';
import type { GameContext } from '../../core/context';
import { Rng } from '../../core/rng';
import type { System } from '../../core/system';
import { MOODS, onMood, type Mood } from '../../render/mood';
import { onQualityTier, qualityTier as qualityTierNow } from '../../render/qualityTier';
import type { QuarksFx } from '../../fx/quarksFx';
import { vfxBudget } from '../../fx/vfxTuning';

/**
 * Pooled GPU point particles: two draw calls total (alpha-blended smoke/debris + additive glow).
 * Any module can emit through the `particles` service:
 *
 *   ctx.services.tryGet('particles')?.emit('sparks', { x, y: 0.5, z }, { count: 12, dir: { x: 1, z: 0 } });
 *
 * Visual-only: uses its own RNG, never affects the simulation.
 *
 * On MEDIUM / HIGH the showy kinds (smoke, dust, fire, explosions, sparks, glass, flashes, muzzle) are
 * drawn by the three.quarks layer (fx/quarksFx.ts, loaded lazily, up to 3 more draw calls while effects
 * play); LOW and anything quarks does not take stay on these pools.
 */
export type ParticleKind =
  | 'tyreSmoke'
  | 'dust'
  | 'sparks'
  | 'engineSmoke'
  | 'blackSmoke'
  | 'fire'
  | 'explosion'
  | 'debris'
  | 'glass'
  | 'embers'
  | 'impactFlash'
  /** A few warm-white bits thrown off a landed blow, mostly along `dir` (fx/meleeFx.ts). */
  | 'motes'
  /** One still bright bead that dies in a tenth of a second; a row of them along an arc is a slash. */
  | 'streak'
  | 'splash'
  | 'ripple'
  | 'exhaust'
  /** Muzzle flash tongues down `dir` (quarks only; LOW keeps the weapon's own flash mesh). */
  | 'muzzle';

export interface P3 {
  x: number;
  y: number;
  z: number;
}

export interface EmitOptions {
  count?: number;
  /** Base velocity (inherited motion). */
  vel?: P3;
  /** Preferred direction on the ground plane for directional kinds (sparks). */
  dir?: { x: number; z: number };
  /** 0..1+ scales size/speed/alpha. */
  intensity?: number;
  /** Override colour (hex). */
  color?: number;
}

export interface ParticleService {
  emit(kind: ParticleKind, pos: P3, opts?: EmitOptions): void;
  /** Brief point-light flash (explosions, muzzle flashes). */
  flash(pos: P3, intensity: number, duration: number, color?: number): void;
  readonly alive: number;
}

declare module '../../core/services' {
  interface ServiceMap {
    particles: ParticleService;
  }
}

interface Pool {
  cap: number;
  count: number;
  pos: Float32Array;
  vel: Float32Array;
  life: Float32Array;
  maxLife: Float32Array;
  size0: Float32Array;
  size1: Float32Array;
  col: Float32Array;
  alpha0: Float32Array;
  drag: Float32Array;
  lift: Float32Array;
  bounce: Uint8Array;
  attrPos: THREE.BufferAttribute;
  attrCol: THREE.BufferAttribute;
  attrSize: THREE.BufferAttribute;
  geo: THREE.BufferGeometry;
  points: THREE.Points;
  material: THREE.ShaderMaterial;
}

const VERT = /* glsl */ `
  attribute vec4 aColor;
  attribute float aSize;
  uniform float uScale;
  varying vec4 vColor;
  void main() {
    vColor = aColor;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * uScale / max(-mv.z, 1.0);
    gl_Position = projectionMatrix * mv;
  }
`;
const FRAG_SOFT = /* glsl */ `
  uniform vec3 uLight;
  varying vec4 vColor;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float a = smoothstep(0.5, 0.08, d);
    // a little lumpiness so smoke doesn't look like perfect discs
    a *= 0.82 + 0.18 * sin((gl_PointCoord.x + gl_PointCoord.y) * 9.0);
    gl_FragColor = vec4(vColor.rgb * uLight, vColor.a * a);
    #include <colorspace_fragment>
  }
`;
const FRAG_GLOW = /* glsl */ `
  varying vec4 vColor;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float a = pow(1.0 - d * 2.0, 1.6);
    gl_FragColor = vec4(vColor.rgb * a * vColor.a, 1.0);
    #include <colorspace_fragment>
  }
`;

const lumOf = (hex: number): number => {
  const c = tmpLum.setHex(hex);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
};
const tmpLum = new THREE.Color();
/** Rough ambient light on an unlit puff: sky fill plus a share of the sun, through the exposure. */
const moodLight = (m: Mood): number => (lumOf(m.hemi.sky) * m.hemi.intensity + 0.35 * lumOf(m.sun.color) * m.sun.intensity) * m.exposure;
const DAY_LIGHT = moodLight(MOODS.overcast);

/**
 * Smoke, dust, steam and splashes are unlit points with fixed colours, so at night a pale exhaust or
 * vent puff read as a lamp-lit disc on a dark road. They now take the mood's ambient light (relative to
 * the overcast day, which keeps its look exactly) with a slight cast of the sky colour, never below a
 * floor so a puff under a street lamp still shows. Glow particles (sparks, fire) are emissive and keep theirs.
 */
export function particleLight(m: Mood, out: THREE.Color): THREE.Color {
  const k = Math.min(1, Math.max(0.2, moodLight(m) / DAY_LIGHT));
  const sky = tmpLum.setHex(m.hemi.sky);
  const l = Math.max(1e-3, 0.2126 * sky.r + 0.7152 * sky.g + 0.0722 * sky.b);
  // a quarter of the sky's hue (normalised to its own luminance), only as the light drops
  const tint = 0.25 * (1 - k);
  return out.setRGB(k * (1 - tint + (tint * sky.r) / l), k * (1 - tint + (tint * sky.g) / l), k * (1 - tint + (tint * sky.b) / l));
}

function makePool(cap: number, additive: boolean): Pool {
  const geo = new THREE.BufferGeometry();
  const attrPos = new THREE.BufferAttribute(new Float32Array(cap * 3), 3).setUsage(THREE.DynamicDrawUsage);
  const attrCol = new THREE.BufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
  const attrSize = new THREE.BufferAttribute(new Float32Array(cap), 1).setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('position', attrPos);
  geo.setAttribute('aColor', attrCol);
  geo.setAttribute('aSize', attrSize);
  geo.setDrawRange(0, 0);
  const material = new THREE.ShaderMaterial({
    uniforms: { uScale: { value: 600 }, uLight: { value: new THREE.Color(1, 1, 1) } },
    vertexShader: VERT,
    fragmentShader: additive ? FRAG_GLOW : FRAG_SOFT,
    transparent: true,
    depthWrite: false,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
  });
  const points = new THREE.Points(geo, material);
  points.frustumCulled = false;
  points.renderOrder = additive ? 20 : 10;
  return {
    cap,
    count: 0,
    pos: new Float32Array(cap * 3),
    vel: new Float32Array(cap * 3),
    life: new Float32Array(cap),
    maxLife: new Float32Array(cap),
    size0: new Float32Array(cap),
    size1: new Float32Array(cap),
    col: new Float32Array(cap * 3),
    alpha0: new Float32Array(cap),
    drag: new Float32Array(cap),
    lift: new Float32Array(cap),
    bounce: new Uint8Array(cap),
    attrPos,
    attrCol,
    attrSize,
    geo,
    points,
    material,
  };
}

interface Spawn {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  size0: number;
  size1: number;
  color: number;
  alpha: number;
  drag: number;
  lift: number;
  bounce?: boolean;
}

const tmpCol = new THREE.Color();

function spawn(p: Pool, s: Spawn): void {
  if (p.count >= p.cap) return;
  const i = p.count++;
  p.pos[i * 3] = s.x;
  p.pos[i * 3 + 1] = s.y;
  p.pos[i * 3 + 2] = s.z;
  p.vel[i * 3] = s.vx;
  p.vel[i * 3 + 1] = s.vy;
  p.vel[i * 3 + 2] = s.vz;
  p.life[i] = 0;
  p.maxLife[i] = s.life;
  p.size0[i] = s.size0;
  p.size1[i] = s.size1;
  tmpCol.setHex(s.color);
  p.col[i * 3] = tmpCol.r;
  p.col[i * 3 + 1] = tmpCol.g;
  p.col[i * 3 + 2] = tmpCol.b;
  p.alpha0[i] = s.alpha;
  p.drag[i] = s.drag;
  p.lift[i] = s.lift;
  p.bounce[i] = s.bounce ? 1 : 0;
}

function update(p: Pool, dt: number, glow: boolean): void {
  let i = 0;
  while (i < p.count) {
    p.life[i]! += dt;
    if (p.life[i]! >= p.maxLife[i]!) {
      // swap-remove
      const last = --p.count;
      if (i !== last) {
        for (let k = 0; k < 3; k++) {
          p.pos[i * 3 + k] = p.pos[last * 3 + k]!;
          p.vel[i * 3 + k] = p.vel[last * 3 + k]!;
          p.col[i * 3 + k] = p.col[last * 3 + k]!;
        }
        p.life[i] = p.life[last]!;
        p.maxLife[i] = p.maxLife[last]!;
        p.size0[i] = p.size0[last]!;
        p.size1[i] = p.size1[last]!;
        p.alpha0[i] = p.alpha0[last]!;
        p.drag[i] = p.drag[last]!;
        p.lift[i] = p.lift[last]!;
        p.bounce[i] = p.bounce[last]!;
      }
      continue;
    }
    const d = 1 / (1 + p.drag[i]! * dt);
    p.vel[i * 3] = p.vel[i * 3]! * d;
    p.vel[i * 3 + 1] = p.vel[i * 3 + 1]! * d + p.lift[i]! * dt;
    p.vel[i * 3 + 2] = p.vel[i * 3 + 2]! * d;
    p.pos[i * 3] = p.pos[i * 3]! + p.vel[i * 3]! * dt;
    p.pos[i * 3 + 1] = p.pos[i * 3 + 1]! + p.vel[i * 3 + 1]! * dt;
    p.pos[i * 3 + 2] = p.pos[i * 3 + 2]! + p.vel[i * 3 + 2]! * dt;
    if (p.pos[i * 3 + 1]! < 0.03) {
      p.pos[i * 3 + 1] = 0.03;
      if (p.bounce[i]) {
        p.vel[i * 3 + 1] = Math.abs(p.vel[i * 3 + 1]!) * 0.3;
        p.vel[i * 3] = p.vel[i * 3]! * 0.6;
        p.vel[i * 3 + 2] = p.vel[i * 3 + 2]! * 0.6;
      } else p.vel[i * 3 + 1] = 0;
    }
    i++;
  }
  const pos = p.attrPos.array as Float32Array;
  const col = p.attrCol.array as Float32Array;
  const size = p.attrSize.array as Float32Array;
  for (let j = 0; j < p.count; j++) {
    const t = p.life[j]! / p.maxLife[j]!;
    pos[j * 3] = p.pos[j * 3]!;
    pos[j * 3 + 1] = p.pos[j * 3 + 1]!;
    pos[j * 3 + 2] = p.pos[j * 3 + 2]!;
    const fadeIn = glow ? 1 : Math.min(1, t * 8);
    const fadeOut = glow ? 1 - t * t : 1 - t;
    col[j * 4] = p.col[j * 3]!;
    col[j * 4 + 1] = p.col[j * 3 + 1]!;
    col[j * 4 + 2] = p.col[j * 3 + 2]!;
    col[j * 4 + 3] = p.alpha0[j]! * fadeIn * fadeOut;
    size[j] = p.size0[j]! + (p.size1[j]! - p.size0[j]!) * t;
  }
  p.geo.setDrawRange(0, p.count);
  p.attrPos.needsUpdate = true;
  p.attrCol.needsUpdate = true;
  p.attrSize.needsUpdate = true;
}

export function createParticleSystem(): System {
  let ctx: GameContext;
  const rng = new Rng(0xf1f1);
  const soft = makePool(2400, false);
  const glow = makePool(1600, true);
  const light = new THREE.PointLight(0xffa040, 0, 26, 1.6);
  let flashT = 0;
  let flashDur = 1;
  let flashPeak = 0;
  let unsubMood: (() => void) | null = null;
  let unsubTier: (() => void) | null = null;
  let quarks: QuarksFx | null = null;
  let quarksOn = false;
  let quarksLoading = false;
  let disposed = false;

  const r = (lo: number, hi: number): number => rng.range(lo, hi);

  const service: ParticleService = {
    get alive() {
      return soft.count + glow.count + (quarks?.alive ?? 0);
    },
    flash(pos, intensity, duration, color = 0xffa040) {
      light.position.set(pos.x, pos.y + 2, pos.z);
      light.color.setHex(color);
      flashPeak = intensity;
      flashDur = duration;
      flashT = 0;
    },
    emit(kind, p, opts = {}) {
      if (quarksOn && quarks && quarks.emit(kind, p, opts)) return;
      const k = opts.intensity ?? 1;
      const vx = opts.vel?.x ?? 0;
      const vy = opts.vel?.y ?? 0;
      const vz = opts.vel?.z ?? 0;
      const n = opts.count ?? 1;
      for (let i = 0; i < n; i++) {
        switch (kind) {
          case 'tyreSmoke':
            spawn(soft, {
              x: p.x + r(-0.15, 0.15), y: 0.25, z: p.z + r(-0.15, 0.15),
              vx: vx * 0.2 + r(-0.6, 0.6), vy: r(0.3, 0.9), vz: vz * 0.2 + r(-0.6, 0.6),
              life: r(1.2, 2.2) * (0.6 + 0.4 * k), size0: 0.7, size1: r(2.6, 3.8) * (0.7 + 0.3 * k),
              color: opts.color ?? 0xcfccc6, alpha: 0.38 * Math.min(k, 1.2), drag: 1.6, lift: 0.25,
            });
            break;
          case 'dust':
            spawn(soft, {
              x: p.x + r(-0.3, 0.3), y: 0.2, z: p.z + r(-0.3, 0.3),
              vx: vx * 0.3 + r(-1, 1), vy: r(0.2, 0.7), vz: vz * 0.3 + r(-1, 1),
              life: r(0.8, 1.5), size0: 0.6, size1: 2.2, color: opts.color ?? 0x9a8f7c, alpha: 0.3 * k, drag: 2, lift: 0.1,
            });
            break;
          case 'sparks': {
            const dx = opts.dir?.x ?? r(-1, 1);
            const dz = opts.dir?.z ?? r(-1, 1);
            const sp = r(4, 11) * k;
            spawn(glow, {
              x: p.x, y: p.y, z: p.z,
              vx: vx * 0.5 + dx * sp + r(-2.5, 2.5), vy: vy + r(1, 4.5), vz: vz * 0.5 + dz * sp + r(-2.5, 2.5),
              life: r(0.18, 0.5), size0: r(0.12, 0.22), size1: 0.05, color: opts.color ?? 0xffc45a, alpha: 2.2, drag: 1.5, lift: -14,
              bounce: true,
            });
            break;
          }
          case 'impactFlash':
            // A single short, bright pop at the contact point (pooled like everything else).
            spawn(glow, {
              x: p.x, y: p.y, z: p.z, vx: 0, vy: 0.5, vz: 0,
              life: r(0.08, 0.14), size0: 2.2 * k, size1: 0.4, color: 0xffe3a0, alpha: 1.8, drag: 4, lift: 0,
            });
            break;
          case 'motes': {
            const dx = opts.dir?.x ?? 0;
            const dz = opts.dir?.z ?? 0;
            const sp = r(1.2, 3.2) * k;
            spawn(glow, {
              x: p.x + r(-0.08, 0.08), y: p.y + r(-0.08, 0.08), z: p.z + r(-0.08, 0.08),
              vx: dx * sp + r(-1.2, 1.2), vy: r(0.6, 2.2), vz: dz * sp + r(-1.2, 1.2),
              life: r(0.18, 0.32), size0: r(0.07, 0.11), size1: 0.03, color: opts.color ?? 0xfff0d8, alpha: 1.5, drag: 2.5, lift: -8,
            });
            break;
          }
          case 'streak':
            spawn(glow, {
              x: p.x, y: p.y, z: p.z, vx, vy, vz,
              life: 0.1, size0: 0.26 * k, size1: 0.08, color: opts.color ?? 0xffffff, alpha: 1.4, drag: 0, lift: 0,
            });
            break;
          case 'splash':
            // Tiny cool-grey droplets kicked up by a foot in a puddle; fall back under gravity.
            spawn(soft, {
              x: p.x + r(-0.12, 0.12), y: 0.08, z: p.z + r(-0.12, 0.12),
              vx: r(-1.1, 1.1) * k, vy: r(1.2, 2.6) * k, vz: r(-1.1, 1.1) * k,
              life: r(0.22, 0.42), size0: r(0.07, 0.12), size1: 0.04, color: opts.color ?? 0xaebccb, alpha: 0.8, drag: 1.2, lift: -9,
            });
            break;
          case 'ripple':
            // A faint disc that grows and fades on the wet ground (points face the high camera).
            spawn(soft, {
              x: p.x, y: 0.04, z: p.z, vx: 0, vy: 0, vz: 0,
              life: r(0.45, 0.65), size0: 0.15, size1: 0.85 + 0.35 * k, color: opts.color ?? 0xb8c4d2, alpha: 0.2, drag: 0, lift: 0,
            });
            break;
          case 'exhaust': {
            // A small puff from the tail pipe: blown back, curling up and spreading out.
            const dx = opts.dir?.x ?? 0;
            const dz = opts.dir?.z ?? 0;
            const sp = r(0.6, 1.4);
            spawn(soft, {
              x: p.x + r(-0.04, 0.04), y: p.y, z: p.z + r(-0.04, 0.04),
              vx: vx * 0.35 + dx * sp + r(-0.2, 0.2), vy: r(0.15, 0.45), vz: vz * 0.35 + dz * sp + r(-0.2, 0.2),
              life: r(1.1, 2.0), size0: r(0.2, 0.35), size1: r(1.4, 2.2) * (0.7 + 0.3 * k), color: opts.color ?? 0xa4a8ae,
              alpha: 0.45 * Math.min(k, 1.6), drag: 1.8, lift: 0.35,
            });
            break;
          }
          case 'engineSmoke':
            spawn(soft, {
              x: p.x + r(-0.3, 0.3), y: p.y, z: p.z + r(-0.3, 0.3),
              vx: vx * 0.4 + r(-0.3, 0.3), vy: r(1.2, 2.2), vz: vz * 0.4 + r(-0.3, 0.3),
              life: r(1.6, 2.8), size0: 0.8, size1: r(3, 4.5), color: opts.color ?? 0x8f8c88, alpha: 0.32 * k, drag: 1.2, lift: 0.4,
            });
            break;
          case 'blackSmoke':
            spawn(soft, {
              x: p.x + r(-0.4, 0.4), y: p.y, z: p.z + r(-0.4, 0.4),
              vx: vx * 0.3 + r(-0.4, 0.4), vy: r(2, 3.5), vz: vz * 0.3 + r(-0.4, 0.4),
              life: r(2.2, 3.8), size0: 1.2, size1: r(4.5, 6.5), color: opts.color ?? 0x1c1a19, alpha: 0.55 * k, drag: 1, lift: 0.5,
            });
            break;
          case 'fire':
            spawn(glow, {
              x: p.x + r(-0.5, 0.5), y: p.y + r(0, 0.3), z: p.z + r(-0.5, 0.5),
              vx: vx * 0.3 + r(-0.4, 0.4), vy: r(1.5, 3.2), vz: vz * 0.3 + r(-0.4, 0.4),
              life: r(0.35, 0.8), size0: r(0.9, 1.6) * k, size1: 0.3, color: opts.color ?? (rng.chance(0.5) ? 0xff7a1a : 0xffb030), alpha: 1.4,
              drag: 1.5, lift: 1.5,
            });
            break;
          case 'embers':
            spawn(glow, {
              x: p.x + r(-0.6, 0.6), y: p.y, z: p.z + r(-0.6, 0.6),
              vx: r(-0.6, 0.6), vy: r(1, 3), vz: r(-0.6, 0.6),
              life: r(0.8, 1.8), size0: 0.12, size1: 0.04, color: 0xff6a20, alpha: 1.6, drag: 0.8, lift: 0.6,
            });
            break;
          case 'explosion': {
            const a = r(0, Math.PI * 2);
            const sp = r(3, 14) * k;
            spawn(glow, {
              x: p.x, y: p.y + r(0, 1), z: p.z,
              vx: Math.cos(a) * sp, vy: r(2, 9), vz: Math.sin(a) * sp,
              life: r(0.4, 0.9), size0: r(2.2, 4) * k, size1: 0.6, color: rng.chance(0.4) ? 0xffe08a : 0xff6a10, alpha: 1.6, drag: 3.5,
              lift: 2,
            });
            break;
          }
          case 'debris': {
            const a = r(0, Math.PI * 2);
            const sp = r(2, 8) * k;
            spawn(soft, {
              x: p.x, y: p.y, z: p.z,
              vx: vx * 0.5 + Math.cos(a) * sp, vy: r(2, 6), vz: vz * 0.5 + Math.sin(a) * sp,
              life: r(1.5, 3), size0: r(0.18, 0.32), size1: 0.18, color: opts.color ?? 0x2a2622, alpha: 1, drag: 0.4, lift: -16,
              bounce: true,
            });
            break;
          }
          case 'glass': {
            const a = r(0, Math.PI * 2);
            const sp = r(1.5, 5) * k;
            spawn(glow, {
              x: p.x, y: p.y, z: p.z,
              vx: vx * 0.5 + Math.cos(a) * sp, vy: r(1, 4), vz: vz * 0.5 + Math.sin(a) * sp,
              life: r(0.5, 1.1), size0: 0.11, size1: 0.07, color: opts.color ?? 0x9fd4ff, alpha: 1.2, drag: 0.6, lift: -14, bounce: true,
            });
            break;
          }
        }
      }
    },
  };

  return {
    id: 'particles',
    order: 730,
    init(c) {
      ctx = c;
      c.services.provide('particles', service);
      c.scene.add(soft.points, glow.points, light);
      unsubMood = onMood((m) => particleLight(m, soft.material.uniforms.uLight!.value as THREE.Color));
      c.hooks.registerSnapshot('particles', () => ({ alive: service.alive, quarks: quarksOn ? (quarks?.alive ?? 0) : null, vfxCalls: quarks?.drawCalls ?? 0 }));
      // quarks needs a canvas for its atlas fallback; skipped headless (unit tests) and never fetched on LOW.
      if (typeof document !== 'undefined') {
        unsubTier = onQualityTier((q) => {
          const b = vfxBudget(q);
          quarksOn = b !== null;
          if (!b) return;
          if (quarks) quarks.setBudget(b);
          else if (!quarksLoading) {
            quarksLoading = true;
            void Promise.all([import('../../fx/quarksFx'), import('../../fx/fxAtlas').then((m) => m.loadFxAtlas())])
              .then(([{ createQuarksFx }, atlas]) => {
                if (disposed) {
                  atlas.dispose();
                  return;
                }
                quarks = createQuarksFx(c.scene, soft.material.uniforms.uLight!.value as THREE.Color, vfxBudget(qualityTierNow()) ?? b, atlas);
                // Pre-warm (docs/PERF.md "Frame pacing and hitches"): a tiny particle in every batch at the camera
                // focus makes them drawable for a frame, so their programs compile in the renderer's warm-up pass
                // and the atlas uploads now, not on the first shot or explosion.
                const f = c.services.tryGet('cameraRig')?.lookAt ?? { x: 0, z: 0 };
                quarks.prewarm({ x: f.x, y: 1, z: f.z });
                void c.services.tryGet('renderPipeline')?.prewarm?.();
              })
              .catch(() => {
                quarksOn = false;
              });
          }
        });
      }
    },
    update(dt) {
      const h = ctx.render ? ctx.render.canvas.height : 720;
      const scale = h / (2 * Math.tan((ctx.camera.fov * Math.PI) / 360));
      soft.material.uniforms.uScale!.value = scale;
      glow.material.uniforms.uScale!.value = scale;
      update(soft, dt, false);
      update(glow, dt, true);
      quarks?.update(dt);
      if (flashPeak > 0) {
        flashT += dt;
        const t = flashT / flashDur;
        light.intensity = t >= 1 ? 0 : flashPeak * (1 - t) * (1 - t);
        if (t >= 1) flashPeak = 0;
      }
    },
    dispose() {
      disposed = true;
      unsubMood?.();
      unsubTier?.();
      quarks?.dispose();
      quarks = null;
      ctx.scene.remove(soft.points, glow.points, light);
      for (const p of [soft, glow]) {
        p.geo.dispose();
        p.material.dispose();
      }
      light.dispose();
    },
  };
}
